import React, { useEffect, useMemo, useRef } from 'react';
import Card from '@common/components/card';
import { QuestionIcon } from '@modules/performance/components/question-icon/question-icon';
import { PerformanceQuestionType } from '@modules/performance/definitions';
import { useEvaluationStore } from './store';
import { useTranslation } from 'react-i18next';
import Stack from '@common/components/stack';
import { useAppSelector } from '@common/hooks';
import * as loggedUserSelector from '@modules/core/selectors/logged-user';
import AnswerCommentBox from '@modules/performance/components/answer-comment-box/answer-comment-box';
import Icon from '@common/components/icon';
import { useStickyHeader } from './hooks/use-sticky-header';

const QuestionContainer = ({ componentId }: { componentId?: string }) => {
  const { t } = useTranslation();
  const {
    componentsByType,
    responsesByComponentId,
    evaluation,
    setComponentAnswer,
    validationErrorsByComponentId,
  } = useEvaluationStore();

  const loggedUser = useAppSelector(loggedUserSelector.selected);

  const reviewer = evaluation?.data?.reviewers?.[0];
  const reviewerId = reviewer?.id ? String(reviewer.id) : undefined;
  const reviewee = evaluation?.data?.reviewee;
  const revieweeId = reviewee?.id ? String(reviewee.id) : undefined;
  const isReviewerViewing =
    String(loggedUser?.id || '') === String(reviewerId || '');
  // const isRevieweeViewing =
  //   String(loggedUser?.id || '') === String(revieweeId || '');

  // Read-only state: if both reviewer and reviewee submitted or review completed
  const selfSubmitted =
    evaluation?.data?.reviewee?.status === 'submitted' ||
    Boolean(evaluation?.data?.reviewee?.submitted_at);
  const managerSubmitted = Array.isArray(evaluation?.data?.reviewers)
    ? evaluation?.data?.reviewers.some(
        (r: any) => r?.status === 'submitted' || Boolean(r?.submitted_at)
      )
    : false;
  const isCompleted = Boolean(evaluation?.data?.completed_at);

  const employeeVisibility = evaluation?.data?.form?.employee_visibility;

  // Visibility rules:
  // - Reviewer: can see submitted reviewee responses and always sees own responses
  // - Reviewee: can see manager's submitted response only if THEY have submitted first (to prevent bias) AND manager submitted AND employee_visibility is true
  const shouldShowBothAnswersInDualView = isReviewerViewing
    ? selfSubmitted || isCompleted // Reviewer sees dual view if reviewee has submitted
    : (selfSubmitted && managerSubmitted && employeeVisibility) || isCompleted; // Reviewee sees both only if THEY submitted first AND manager submitted AND employee_visibility

  // Show reviewer's answer to reviewee only if reviewee submitted first, manager submitted and employee_visibility is true
  const shouldShowReviewerAnswerToReviewee =
    !isReviewerViewing &&
    selfSubmitted &&
    managerSubmitted &&
    employeeVisibility;

  const openQuestionComponents = useMemo(() => {
    let list = componentsByType.open_question || [];
    if (componentId) {
      const idStr = String(componentId);
      list = list.filter((i) => String(i.id) === idStr);
    }
    // Filter out manager-only questions for reviewees during evaluation
    // Show them only after completion if answer_visible is true
    if (!isReviewerViewing) {
      list = list.filter((item) => {
        const isManagerOnly =
          Boolean(item?.settings?.visibility?.manager) &&
          !item?.settings?.visibility?.employee;
        if (isManagerOnly) {
          // Show manager-only questions to reviewee only if evaluation is completed and answer_visible is true
          return (
            isCompleted && Boolean(item?.settings?.visibility?.answer_visible)
          );
        }
        return true;
      });
    }
    // Note: Removed the filter that excluded employee-only questions for reviewers
    // Reviewers should now see employee-only questions but cannot edit them
    return list;
  }, [componentsByType, componentId, isReviewerViewing, isCompleted]);

  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { sentinelRef, stickyHeaderRef, isHeaderSticky, defaultTopOffset } = useStickyHeader();
  // Check if all visible questions are manager-only (for reviewees)
  // const shouldHideHeader = useMemo(() => {
  //   if (isReviewerViewing || openQuestionComponents.length === 0) {
  //     return false;
  //   }

  //   if (isRevieweeViewing || openQuestionComponents.length === 0) {
  //     return true;
  //   }
  //   // If reviewee is viewing, check if all questions are manager-only
  //   return openQuestionComponents.every(
  //     (item) => Boolean(item?.settings?.visibility?.manager) &&
  //       !item?.settings?.visibility?.employee
  //   );
  // }, [openQuestionComponents, isReviewerViewing]);

  useEffect(() => {
    const openList = openQuestionComponents.map((i) => String(i.id));
    const firstErrorCid = openList.find(
      (cid) => validationErrorsByComponentId?.[cid]
    );
    if (firstErrorCid) {
      const el = itemRefs.current[firstErrorCid];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [validationErrorsByComponentId, openQuestionComponents]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div ref={sentinelRef} className="tw-h-0 tw-w-full tw-mb-[-16px]" />
      {/* <div
        ref={stickyHeaderRef}
        style={{
          zIndex: 1,
        }}
        className={`${
          isHeaderSticky
            ? '!tw-bg-white !tw-p-5 !tw-border-b tw-border-0 tw-border-solid !tw-border-gray-border' +
              ' tw-sticky tw-top-[203px] tw-transition-all tw-duration-200'
            : ''
        }`}
      >
        <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
          <QuestionIcon
            type={PerformanceQuestionType.OPEN_QUESTION}
            className="!tw-w-4 !tw-h-4"
          />
          <div className="tw-text-gray-textDark tw-text-xl tw-font-semibold">
            {t('performance:open_question')}
          </div>
        </div>
      </div> */}
      {openQuestionComponents.map((item) => {
        const cid = String(item.id);
        const entry = responsesByComponentId[cid];
        // Handle both string and object answer formats
        const getAnswerText = (rawAnswer: any): string => {
          if (!rawAnswer) return '';
          if (typeof rawAnswer === 'string') return rawAnswer;
          if (typeof rawAnswer === 'object') {
            // Handle nested answer object format
            const nested = rawAnswer.answer || rawAnswer;
            if (typeof nested === 'string') return nested;
            if (typeof nested === 'object' && nested.comment)
              return nested.comment;
            return nested || '';
          }
          return String(rawAnswer);
        };

        const reviewerAnswer = reviewerId
          ? getAnswerText(entry?.answersByUserId?.[reviewerId])
          : '';
        const revieweeAnswer = revieweeId
          ? getAnswerText(entry?.answersByUserId?.[revieweeId])
          : '';
        const showBothAnswers =
          item?.settings?.visibility?.employee &&
          item?.settings?.visibility?.manager;
        const isEmployeeOnly =
          Boolean(item?.settings?.visibility?.employee) &&
          !item?.settings?.visibility?.manager;
        const isManagerOnly =
          Boolean(item?.settings?.visibility?.manager) &&
          !item?.settings?.visibility?.employee;
        const showRequiredError = Boolean(
          item?.settings?.answer_required &&
            validationErrorsByComponentId?.[cid]
        );

        return (
          <>
            <div
              ref={stickyHeaderRef}
              style={{
                zIndex: 1,
                '--top-offset': `${defaultTopOffset ?? 203}px`,
              } as React.CSSProperties}
              className={`${
                isHeaderSticky
                  ? '!tw-bg-white !tw-p-5 !tw-border-b tw-border-0 tw-border-solid !tw-border-gray-border' +
                    ' tw-sticky tw-top-[var(--top-offset)] tw-transition-all tw-duration-200'
                  : ''
              }`}
            >
              <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
                <QuestionIcon
                  type={PerformanceQuestionType.OPEN_QUESTION}
                  className="!tw-w-4 !tw-h-4"
                />
                <div className="tw-text-gray-textDark tw-text-xl tw-font-semibold">
                  {t('performance:open_question')}
                </div>
              </div>
            </div>
            <React.Fragment key={cid}>
              {item.settings && (
                <Card>
                  <Card.Content className="tw-flex tw-flex-col !tw-p-5 !tw-gap-5">
                    <div
                      className="tw-flex tw-flex-col tw-gap-3"
                      ref={(el) => {
                        itemRefs.current[cid] = el;
                      }}
                    >
                      <Stack direction="row" gap={3}>
                        <Stack direction="column" gap={1}>
                          <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                            <div className="tw-text-gray-textDark tw-text-xl tw-font-semibold">
                              {item?.settings?.text || ''}
                            </div>
                            {item?.settings?.answer_required && (
                              <span className="Screen__Component__Question__Text__Required">
                                *
                              </span>
                            )}
                          </div>
                          {item?.settings?.description && (
                            <div
                              className={`tw-text-gray-muted tw-text-base tw-font-medium ${
                                item?.settings?.description
                                  ? 'tw-line-clamp-2'
                                  : 'tw-line-clamp-none'
                              }`}
                            >
                              {item?.settings?.description}
                            </div>
                          )}
                        </Stack>
                      </Stack>

                      <Stack direction="row" gap={2}>
                        <div
                          className="tw-flex tw-items-center tw-px-[10px] tw-py-1
         tw-rounded-[25px] tw-border tw-border-solid tw-border-gray-border tw-w-fit"
                        >
                          <span className="tw-text-sm tw-font-medium tw-text-gray-text">
                            {item?.settings?.visibility?.employee &&
                            item?.settings?.visibility?.manager
                              ? t('performance:manager_and_employee')
                              : item?.settings?.visibility?.employee
                              ? t('performance:employee_only')
                              : item?.settings?.visibility?.manager
                              ? t('performance:manager_only')
                              : t('performance:visible_to_all')}
                          </span>
                        </div>
                        {isReviewerViewing &&
                          item?.settings?.visibility?.manager &&
                          !item?.settings?.visibility?.answer_visible && (
                            <div className="tw-flex tw-items-center tw-gap-2
                            !tw-bg-gray-neutral-50 tw-rounded-full tw-px-2 tw-py-1"
                            >
                              <Icon
                                type="eye_off"
                                customClassName="!tw-w-4 !tw-h-4"
                              />
                              <span className="tw-text-sm tw-font-medium tw-text-gray-text">
                                {t('performance:not_visible_to_employee')}
                              </span>
                            </div>
                          )}
                      </Stack>
                    </div>

                    <div className="tw-flex tw-flex-col tw-gap-3">
                      {isEmployeeOnly && isReviewerViewing ? (
                        /* Employee-only questions: Reviewers can only see the reviewee's answer */
                        <AnswerCommentBox
                          userFullName={reviewee?.full_name || ''}
                          answer={revieweeAnswer || ''}
                          isReadOnly
                          canEdit={false}
                          showAsNotSubmitted={!selfSubmitted}
                          enableInlineEdit={false}
                        />
                      ) : isManagerOnly && !isReviewerViewing ? (
                        /* Manager-only questions: Reviewees can see manager's answer after completion if answer_visible is true */
                        <AnswerCommentBox
                          userFullName={reviewer?.full_name || ''}
                          answer={reviewerAnswer || ''}
                          isReadOnly
                          canEdit={false}
                          showAsNotSubmitted={!reviewerAnswer}
                          enableInlineEdit={false}
                        />
                      ) : showBothAnswers ? (
                        <>
                          {/* Show both answers based on visibility rules */}
                          {shouldShowBothAnswersInDualView ? (
                            <>
                              {/* Reviewee Answer */}
                              <AnswerCommentBox
                                userFullName={reviewee?.full_name || ''}
                                answer={revieweeAnswer || ''}
                                isReadOnly={
                                  isReviewerViewing ||
                                  selfSubmitted ||
                                  isCompleted
                                }
                                canEdit={
                                  !isReviewerViewing &&
                                  !selfSubmitted &&
                                  !isCompleted
                                }
                                showAsNotSubmitted={
                                  !selfSubmitted && !revieweeAnswer
                                }
                                enableInlineEdit
                                onSave={(newAnswer) => {
                                  if (revieweeId) {
                                    setComponentAnswer(
                                      cid,
                                      revieweeId,
                                      newAnswer
                                    );
                                  }
                                }}
                              />
                              {/* Reviewer Answer */}
                              {(item?.settings?.visibility?.answer_visible ||
                                isReviewerViewing) && (
                                <AnswerCommentBox
                                  userFullName={reviewer?.full_name || ''}
                                  answer={
                                    isReviewerViewing ||
                                    shouldShowReviewerAnswerToReviewee
                                      ? reviewerAnswer
                                      : ''
                                  }
                                  isReadOnly={!isReviewerViewing || isCompleted}
                                  canEdit={isReviewerViewing && !isCompleted}
                                  showAsNotSubmitted={
                                    !isReviewerViewing &&
                                    !shouldShowReviewerAnswerToReviewee
                                  }
                                  enableInlineEdit
                                  onSave={(newAnswer) => {
                                    if (reviewerId) {
                                      setComponentAnswer(
                                        cid,
                                        reviewerId,
                                        newAnswer
                                      );
                                    }
                                  }}
                                />
                              )}
                            </>
                          ) : (
                            /* Show only logged user's own answer when both haven't submitted */
                            <>
                              {isReviewerViewing && (
                                <AnswerCommentBox
                                  userFullName={reviewer?.full_name || ''}
                                  answer={reviewerAnswer}
                                  isReadOnly={isCompleted}
                                  canEdit={!isCompleted}
                                  enableInlineEdit
                                  onSave={(newAnswer) => {
                                    if (reviewerId) {
                                      setComponentAnswer(
                                        cid,
                                        reviewerId,
                                        newAnswer
                                      );
                                    }
                                  }}
                                />
                              )}

                              {!isReviewerViewing && (
                                <AnswerCommentBox
                                  userFullName={reviewee?.full_name || ''}
                                  answer={revieweeAnswer}
                                  isReadOnly={selfSubmitted || isCompleted}
                                  canEdit={!selfSubmitted && !isCompleted}
                                  enableInlineEdit
                                  onSave={(newAnswer) => {
                                    if (revieweeId) {
                                      setComponentAnswer(
                                        cid,
                                        revieweeId,
                                        newAnswer
                                      );
                                    }
                                  }}
                                />
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        /* Single answer view - show only logged user's own answer */
                        <>
                          {isReviewerViewing && (
                            <AnswerCommentBox
                              userFullName={reviewer?.full_name || ''}
                              answer={reviewerAnswer}
                              isReadOnly={isCompleted}
                              canEdit={!isCompleted}
                              enableInlineEdit
                              onSave={(newAnswer) => {
                                if (reviewerId) {
                                  setComponentAnswer(
                                    cid,
                                    reviewerId,
                                    newAnswer
                                  );
                                }
                              }}
                            />
                          )}

                          {!isReviewerViewing && (
                            <AnswerCommentBox
                              userFullName={reviewee?.full_name || ''}
                              answer={revieweeAnswer}
                              isReadOnly={selfSubmitted || isCompleted}
                              canEdit={!selfSubmitted && !isCompleted}
                              enableInlineEdit
                              onSave={(newAnswer) => {
                                if (revieweeId) {
                                  setComponentAnswer(
                                    cid,
                                    revieweeId,
                                    newAnswer
                                  );
                                }
                              }}
                            />
                          )}
                        </>
                      )}
                      {showRequiredError && (
                        <div className="tw-text-red-500 tw-mt-1 tw-text-sm">
                          {validationErrorsByComponentId[cid]}
                        </div>
                      )}
                    </div>
                  </Card.Content>
                </Card>
              )}
            </React.Fragment>
          </>
        );
      })}
    </div>
  );
};

export default QuestionContainer;
